import { handleActions, createAction } from "redux-actions";

import {
    axios,
    // axiosWithoutHeaders
} from "../cores/axiosWrapper";
import uniqid from "uniqid";

import configurationTemplate from "./event-trigger-configuration-template";
import configurationTemplateV2 from "./event-trigger-configuration-template-v2";

import replaceCustomTags from "../cores/replaceCustomTags";
import { getDetailDriveFolder } from "./folderTreeView";
import { getCategoryGroup } from "@modules/categoryGroupsManagement";
import compareVersions, { VERSION_CM_9436_EVENT_TRIGGER_VIRTUAL_FOLDER } from "@cores/version";
import { getVocabularyAPI } from "@modules/apis/vocabulary";
import { getPromptPresetAPI } from "@modules/apis/prompt-presets";
import { AUTO_CAPTION_ENGINES } from "@constants";
import { correctPayload } from "./createEventTrigger";

function getEventTriggerAPI(stageId, apiEndpoint, projectId, id) {
    // console.log(stageId, apiEndpoint, projectId, id);
    return axios.get(`${apiEndpoint}/triggers/${id}`, {
        headers: {
            stageId,
            projectId,
        },
    });
}

function deleteEventTriggerAPI(stageId, apiEndpoint, projectId, id) {
    return axios.delete(`${apiEndpoint}/triggers/${id}`, {
        headers: {
            stageId,
            projectId,
        },
    });
}

function updateEventTriggerAPI(stageId, apiEndpoint, projectId, id, data) {
    return axios.put(`${apiEndpoint}/triggers/${id}`, data, {
        headers: {
            stageId,
            projectId,
        },
    });
}

function defineFields(data, stage, existingData = null) {
    const template =
        compareVersions(stage.version, VERSION_CM_9436_EVENT_TRIGGER_VIRTUAL_FOLDER) < 0
            ? configurationTemplate
            : configurationTemplateV2;

    const selectedConfigurationTemplate = template.filter((template) => template.eventType === data.eventType)[0];

    data.conditions = data.conditions.map((condition) => {
        const matchedConditionCustomField = selectedConfigurationTemplate.conditionCustomFields.filter(
            (customField) => customField.key === condition.key,
        )[0];

        const existingCondition = existingData?.conditions?.find((existing) => existing.id === condition.id);

        if (existingCondition) {
            return existingCondition;
        }

        return {
            ...matchedConditionCustomField,
            ...condition,
            uniqKey: existingCondition?.uniqKey || uniqid(),
            isFolded:
                existingCondition?.isFolded !== undefined
                    ? existingCondition.isFolded
                    : !matchedConditionCustomField.required,
        };
    });

    data.actions = data.actions.map((action) => {
        const matchedActionCustomFieldAreas = selectedConfigurationTemplate.actions.filter(
            (templateAction) => templateAction.key === action.key,
        );
        const matchedActionCustomFieldArea =
            matchedActionCustomFieldAreas.length === 1
                ? matchedActionCustomFieldAreas[0]
                : matchedActionCustomFieldAreas.filter((templateAction) => {
                      // 구버전 호환
                      if (!action.payload.type_) return action.payload.type === templateAction.automaticValues.type_;
                      else return action.payload.mediaType === templateAction.automaticValues.mediaType;
                  })[0];

        const existingAction = existingData?.actions?.find((existing) => existing.id === action.id);

        if (existingAction) {
            return existingAction;
        }

        return {
            ...matchedActionCustomFieldArea,
            ...action,
            options: selectedConfigurationTemplate.actions,
            uniqKey: uniqid(),
            customFields:
                matchedActionCustomFieldArea &&
                matchedActionCustomFieldArea.customFields &&
                matchedActionCustomFieldArea.customFields.map((customField) => {
                    const value = action.payload && action.payload[customField.key];

                    // Find existing customField with same id to preserve uniqKey
                    const existingCustomField = existingAction?.customFields?.find(
                        (existing) => existing.id === customField.id,
                    );

                    return {
                        ...customField,
                        defaultValue: value,
                        uniqKey: existingCustomField?.uniqKey || uniqid(),
                        isFolded:
                            existingCustomField?.isFolded !== undefined
                                ? existingCustomField.isFolded
                                : !customField.required,
                        value: value,
                    };
                }),
        };
    });

    return data;
}

const processActionsData = async (actions, dispatch, getState, projectId) => {
    const { stage } = getState();

    return Promise.all(
        actions?.map(async (action) => {
            try {
                let payload = action.payload;

                // Virtual Folder ID가 있는 경우 전체 Folder Path 생성
                if (payload.virtualFolder?.id) {
                    const folderPath = await dispatch(
                        getDetailDriveFolder({
                            driveId: payload.virtualFolder.virtualDrive.id,
                            folderId: payload.virtualFolder.id,
                        }),
                    );

                    payload = {
                        ...payload,
                        virtualFolder: {
                            ...payload.virtualFolder,
                            virtualFolderPath: folderPath?.parents?.reverse()?.reduce((acc, cur) => {
                                return `${cur.name}/${acc}`;
                            }, folderPath.name),
                        },
                    };
                }

                if (payload.isAutoVirtualFolderEnabled) {
                    payload.virtualFolder = payload.virtualFolder ?? {};
                    payload.virtualFolder.isAutoVirtualFolderEnabled = payload.isAutoVirtualFolderEnabled;
                    delete payload.isAutoVirtualFolderEnabled;
                }

                // 카테고리 그룹ID가 있을 경우 카테고리 그룹명 생성
                if (payload.categories?.length > 0 && payload.categories[0].categoryGroup?.id) {
                    const categoryGroup = await dispatch(
                        getCategoryGroup(projectId, payload.categories[0].categoryGroup.id),
                    );

                    payload = {
                        ...payload,
                        categories: action.payload.categories?.map((category) => ({
                            ...category,
                            categoryGroup: { ...category.categoryGroup, name: categoryGroup.name },
                        })),
                    };
                }

                if (payload.autoCaptions) {
                    const { engine, options } =
                        payload.autoCaptions.find((caption) => caption.engine === AUTO_CAPTION_ENGINES.DAGLO_STT) ??
                        payload.autoCaptions[0];
                    const { dictationVocabularyId, ...rest } = options ?? {};

                    const data = {
                        engine: engine.split("_")[0],
                        ...rest,
                        services: payload.autoCaptions.map((item) => {
                            return { serviceType: item.engine };
                        }),
                    };

                    if (dictationVocabularyId) {
                        let { data: vocabularyData } = await getVocabularyAPI(
                            stage.id,
                            stage.endpoint,
                            projectId,
                            dictationVocabularyId,
                        );
                        data.dictationVocabularyName = vocabularyData.name;
                        data.dictationVocabularyId = dictationVocabularyId;
                    }

                    payload.autoCaptions = { useAutoCaption: "USED", data };
                }

                if (payload.attributions) {
                    payload.attributions.forEach((attr, index) => {
                        attr.id = index;
                    });
                }

                // AI(prompt preset) 항목을 aiAnalysis에 병합 (이름 조회 포함)
                if (payload.AI && payload.AI.length > 0) {
                    const presetItems = await Promise.all(
                        payload.AI.map(async (aiItem) => {
                            try {
                                const presetResponse = await getPromptPresetAPI(
                                    stage.endpoint,
                                    projectId,
                                    aiItem.promptPresetId,
                                );
                                return {
                                    engineId: aiItem.engineId,
                                    promptPresetId: aiItem.promptPresetId,
                                    name: presetResponse.data?.name || "",
                                };
                            } catch (e) {
                                console.error("Failed to fetch prompt preset:", aiItem.promptPresetId, e);
                                return {
                                    engineId: aiItem.engineId,
                                    promptPresetId: aiItem.promptPresetId,
                                    name: "",
                                };
                            }
                        }),
                    );

                    payload.aiAnalysis = [...(payload.aiAnalysis || []), ...presetItems];
                    delete payload.AI;
                }

                return { ...action, payload };
            } catch (e) {
                console.error(e);
                return e;
            }
        }),
    );
};

//action type
const RESET = "RESET";

const EVENT_TRIGGER_MANAGEMENT_DETAIL_PENDING = "EVENT_TRIGGER_MANAGEMENT_DETAIL_PENDING";
const EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE = "EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE";

const EVENT_TRIGGER_MANAGEMENT_DETAIL_IS_CHANGING_DATA = "EVENT_TRIGGER_MANAGEMENT_DETAIL_IS_CHANGING_DATA";

const EVENT_TRIGGER_MANAGEMENT_DETAIL_GET_EVENT_TRIGGER_SUCCESS =
    "EVENT_TRIGGER_MANAGEMENT_DETAIL_GET_EVENT_TRIGGER_SUCCESS";
const EVENT_TRIGGER_MANAGEMENT_DETAIL_UPDATE_EVENT_TRIGGER_SUCCESS =
    "EVENT_TRIGGER_MANAGEMENT_DETAIL_UPDATE_EVENT_TRIGGER_SUCCESS";
const EVENT_TRIGGER_MANAGEMENT_DETAIL_DELETE_EVENT_TRIGGER_SUCCESS =
    "EVENT_TRIGGER_MANAGEMENT_DETAIL_DELETE_EVENT_TRIGGER_SUCCESS";

//reducer
const initialState = {
    data: null,
    pending: false,
    error: false,
    isDeleted: false,
    isChangingData: false,
    selectedConfigurationTemplate: configurationTemplate[0],
};

export const reset = createAction(RESET);

export default handleActions(
    {
        [RESET]: (state, action) => {
            if (!action.payload) return { ...initialState };
            const { stage } = action.payload;

            const template =
                compareVersions(stage.version, VERSION_CM_9436_EVENT_TRIGGER_VIRTUAL_FOLDER) < 0
                    ? configurationTemplate[0]
                    : configurationTemplateV2[0];

            return {
                ...initialState,
                selectedConfigurationTemplate: template,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_PENDING]: (state, action) => {
            return {
                ...state,
                pending: true,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE]: (state, action) => {
            return {
                ...state,
                error: true,
                pending: false,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_IS_CHANGING_DATA]: (state, action) => {
            return {
                ...state,
                isChangingData: true,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_GET_EVENT_TRIGGER_SUCCESS]: (state, action) => {
            const { data, stage } = action.payload;

            return {
                ...state,
                data: defineFields(data, stage, null),
                pending: false,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_DELETE_EVENT_TRIGGER_SUCCESS]: (state, action) => {
            return {
                ...state,
                isDeleted: true,
                isChangingData: false,
            };
        },
        [EVENT_TRIGGER_MANAGEMENT_DETAIL_UPDATE_EVENT_TRIGGER_SUCCESS]: (state, action) => {
            const { data, stage } = action.payload;
            return {
                ...state,
                data: defineFields(data, stage, state.data),
                isChangingData: false,
            };
        },
    },
    initialState,
);

export const getEventTrigger = (projectId, id) => async (dispatch, getState) => {
    const { stage } = getState();

    dispatch({ type: EVENT_TRIGGER_MANAGEMENT_DETAIL_PENDING });

    getEventTriggerAPI(stage.id, stage.endpoint, projectId, id)
        .then(async (response) => {
            if (response.data) {
                const newActions = await processActionsData(response.data.actions, dispatch, getState, projectId);

                dispatch({
                    type: EVENT_TRIGGER_MANAGEMENT_DETAIL_GET_EVENT_TRIGGER_SUCCESS,
                    payload: { ...response, data: { ...response.data, actions: newActions }, stage },
                });
            }
        })
        .catch((error) => {
            dispatch({
                type: EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE,
                payload: error,
            });
        });
};

export const deleteEventTrigger = (projectId, id) => (dispatch, getState) => {
    const { stage } = getState();

    dispatch({ type: EVENT_TRIGGER_MANAGEMENT_DETAIL_IS_CHANGING_DATA });

    deleteEventTriggerAPI(stage.id, stage.endpoint, projectId, id)
        .then((response) => {
            dispatch({
                type: EVENT_TRIGGER_MANAGEMENT_DETAIL_DELETE_EVENT_TRIGGER_SUCCESS,
                payload: response,
            });
        })
        .catch((error) => {
            dispatch({
                type: EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE,
                payload: error,
            });
        });
};

export const updateEventTrigger = (projectId, id, data) => async (dispatch, getState) => {
    const { stage } = getState();
    const parsedData = JSON.parse(JSON.stringify(data));

    dispatch({ type: EVENT_TRIGGER_MANAGEMENT_DETAIL_IS_CHANGING_DATA });

    if (parsedData.actions?.length > 0) {
        parsedData.actions = parsedData.actions.map((action) => {
            const payload = correctPayload(
                action.customFields.reduce((acc, curr) => {
                    acc[curr.key] = curr.value;
                    return acc;
                }, {}),
            );

            return {
                key: action.key,
                payload: {
                    ...payload,
                    ...replaceCustomTags(action.automaticValues, payload),
                },
            };
        });
    }

    try {
        const response = await updateEventTriggerAPI(stage.id, stage.endpoint, projectId, id, parsedData);
        const actions = response.data.actions;
        const newActions = await processActionsData(actions, dispatch, getState, projectId);

        dispatch({
            type: EVENT_TRIGGER_MANAGEMENT_DETAIL_UPDATE_EVENT_TRIGGER_SUCCESS,
            payload: { ...response, data: { ...response.data, actions: newActions }, stage },
        });
        return response;
    } catch (error) {
        dispatch({
            type: EVENT_TRIGGER_MANAGEMENT_DETAIL_FAILURE,
            payload: error,
        });
        throw error;
    }
};
